import React, { useState } from 'react';
import {
  Box, Typography, makeStyles, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, Link, Button,
  Checkbox, FormControlLabel, Divider,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { ReactComponent as StructureIcon } from '../../../layout/icons/smc_fill.svg';
import { useHistory } from 'react-router-dom';
import { downloadReportPDF } from './pdfDownloadUtil';
import { openDoc, coaDoc, bprDoc, deviationDocs, ccDoc } from './mfgDocLinks';
import { selectTabData, DEVIATION_DATA, PLANNED_CC_DATA } from './manufacturingTabData';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';

const useStyles = makeStyles(() => ({
  root: { padding: '8px 4px' },

  // ── Stepper list view ──────────────────────────────────────────
  stepperRow: { display: 'flex', flexDirection: 'row', marginBottom: '24px' },
  stepperLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '16px', minWidth: '28px' },
  stepCircle: {
    width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#1976d2',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: '0.92rem', flexShrink: 0, zIndex: 1,
  },
  stepLine: { flex: 1, width: '2px', backgroundColor: '#1976d2', marginTop: '2px' },
  stepContent: { flex: 1, minWidth: 0 },
  sectionTitle: { fontSize: '0.9rem', fontWeight: 700, color: '#222', marginBottom: '10px', marginTop: '3px' },
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': { fontWeight: 600, fontSize: '0.88rem', color: '#333', padding: '8px 12px', borderBottom: '1px solid #e0e0e0', whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'normal' },
  },
  bodyRow: {
    '& td': { fontSize: '0.88rem', padding: '6px 12px', borderBottom: '1px solid #eeeeee', color: '#444', verticalAlign: 'middle', whiteSpace: 'normal', wordBreak: 'break-word' },
    '&:last-child td': { borderBottom: 'none' },
  },
  idCell: { fontWeight: 500, color: '#333', verticalAlign: 'top !important', paddingTop: '10px !important' },
  downloadLink: {
    color: '#1976d2', display: 'flex', alignItems: 'center', gap: '4px',
    textDecoration: 'none', fontSize: '0.88rem', cursor: 'pointer', marginBottom: '4px',
    '&:hover': { textDecoration: 'underline' }, '&:last-child': { marginBottom: 0 },
  },
  downloadIcon: { fontSize: '0.9rem', color: '#1976d2' },
  reviewBox: { display: 'inline-flex', alignItems: 'flex-start', gap: '6px' },
  reviewIcon: {},
  reviewText: { fontSize: '0.92rem', color: '#444', lineHeight: 1.5 },

  // ── Review detail view ─────────────────────────────────────────
  reviewDetailRoot: { fontFamily: 'inherit', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 280px)', overflow: 'hidden' },
  backBar: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '6px 0 12px', borderBottom: '1px solid #e0e0e0', marginBottom: '12px',
    flexShrink: 0,
  },
  backBtn: { textTransform: 'none', fontSize: '0.9rem', color: '#444', padding: '2px 8px' },
  reviewTitle: { fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' },
  infoBar: {
    backgroundColor: '#EEF4FB', borderRadius: '6px', padding: '12px 24px',
    display: 'flex', gap: '40px', marginBottom: '14px', flexWrap: 'wrap',
    flexShrink: 0,
    width: 'fit-content',
    margin: '0 auto 14px',
  },
  infoBarItem: {},
  infoBarLabel: { fontSize: '0.75rem', fontWeight: 700, color: '#111', marginBottom: '4px' },
  infoBarValue: { fontSize: '0.88rem', color: '#444', fontWeight: 400 },

  splitPane: {
    display: 'flex', gap: '16px', alignItems: 'stretch',
    flex: 1, minHeight: 0, overflow: 'hidden',
    height: 'calc(100vh - 280px)',
  },

  // Left form
  leftPane: { flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  leftHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' },
  leftTitle: { fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' },
  btnEdit: { textTransform: 'none', fontSize: '0.85rem', marginRight: '6px', borderColor: '#1976d2', color: '#1976d2' },
  btnUnedited: { textTransform: 'none', fontSize: '0.85rem', borderColor: '#888', color: '#555' },

  formTable: { width: '100%', borderCollapse: 'collapse' },
  formRow: {
    '& td': { fontSize: '0.85rem', padding: '5px 8px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' },
  },
  formLabel: { color: '#555', fontWeight: 500, width: '38%', fontSize: '0.84rem', paddingRight: '8px' },
  formValue: { color: '#222', fontSize: '0.84rem', flex: 1 },
  formBadge: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '4px', padding: '1px 6px', fontSize: '0.68rem', fontWeight: 700, color: '#fff',
    minWidth: '28px', whiteSpace: 'normal', wordBreak: 'break-word',
  },
  badgeGreen: { backgroundColor: '#4CAF50' },
  badgeOrange: { backgroundColor: '#FF8F00' },
  badgeRed: { backgroundColor: '#d32f2f' },

  legendRow: {
    display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap',
  },
  legendLabel: { fontSize: '0.7rem', color: '#555', fontWeight: 500 },
  legendItem: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem' },
  legendBox: { width: '14px', height: '14px', borderRadius: '2px' },
  noteText: { fontSize: '0.68rem', color: '#777', marginTop: '6px', fontStyle: 'italic' },

  // Right PDF pane
  rightPane: { flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  pdfHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' },
  pdfTitle: { fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' },
  btnDownload: { textTransform: 'none', fontSize: '0.85rem', backgroundColor: '#4CAF50', color: '#fff', '&:hover': { backgroundColor: '#388E3C' } },
  pdfToolbar: {
    display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#555',
    padding: '4px 8px', borderRadius: '4px 4px 0 0', color: '#fff',
  },
  pdfToolbarIcon: { fontSize: '1rem', cursor: 'pointer', color: '#eee', '&:hover': { color: '#fff' } },
  pdfToolbarText: { fontSize: '0.92rem', color: '#ddd' },
  pdfBody: {
    border: '1px solid #ccc', borderTop: 'none', borderRadius: '0 0 4px 4px',
    padding: '16px', backgroundColor: '#fff', flex: 1, minHeight: 0,
  },
  pdfCompany: { fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '2px' },
  pdfLogo: { fontSize: '0.85rem', color: '#1976d2', fontWeight: 600 },
  pdfRef: { fontSize: '0.7rem', color: '#555', marginBottom: '8px' },
  pdfDocTitle: { fontSize: '0.9rem', fontWeight: 700, textAlign: 'center', marginBottom: '12px', color: '#1a1a2e' },
  pdfSection: { fontSize: '0.85rem', fontWeight: 700, color: '#222', marginBottom: '4px', marginTop: '8px' },
  pdfFieldRow: { display: 'flex', gap: '8px', marginBottom: '3px', alignItems: 'flex-start' },
  pdfFieldLabel: { fontSize: '0.92rem', color: '#444', minWidth: '150px' },
  pdfFieldColon: { fontSize: '0.92rem', color: '#444', marginRight: '4px' },
  pdfFieldValue: { fontSize: '0.92rem', color: '#222' },
  pdfHighlight: { backgroundColor: '#ffeb3b', padding: '0 2px', borderRadius: '2px', fontSize: '0.92rem', fontWeight: 600 },
  pdfHighlightRed: { border: '1px solid #d32f2f', padding: '0 4px', borderRadius: '2px', fontSize: '0.92rem', color: '#d32f2f' },
  pdfDesc: { fontSize: '0.92rem', color: '#333', lineHeight: 1.5, marginTop: '4px' },
}));

// Build sections from API documentList response
const buildSectionsFromAPI = (documentList) => {
  const grouped = {};
  documentList.forEach(doc => {
    const cat = doc.documentCategory || 'General';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({
      id: doc.collabrixDocumentID || doc.documentName,
      documents: [{ name: doc.fileName || doc.documentName }],
      review: { fieldsRequire: 0, fieldsReviewed: 0 },
      uploadDate: doc.uploadDate || doc.creationDate || '',
    });
  });
  return Object.keys(grouped).map((cat, idx) => ({
    number: idx + 1,
    title: cat,
    idColumn: 'Document ID',
    rows: grouped[cat],
  }));
};

// Product-specific Deviation + Change Control documents for a batch, reusing the same
// data the Deviations/CC tabs use (manufacturingTabData) + the served-PDF resolvers
// (mfgDocLinks). VV batches -> VV Dev/CC IDs & PDFs; TZB2401-DS -> Herceptin ones.
const dateOnly = (ts) => (ts ? String(ts).split(/[ T]/)[0] : '');
const buildProductSections = (batchNumber) => {
  const deviations = selectTabData(DEVIATION_DATA, batchNumber) || [];
  const ccs = selectTabData(PLANNED_CC_DATA, batchNumber) || [];
  const sections = [];

  const devRows = deviations.map((d) => {
    const served = deviationDocs(batchNumber, d) || {};
    const documents = [];
    if (d.assessmentReport?.name || served.assessment) {
      documents.push({ name: d.assessmentReport?.name || 'Deviation Assessment Report.pdf', servedFile: served.assessment || null });
    }
    if (d.finalReport?.name || served.final) {
      documents.push({ name: d.finalReport?.name || 'Final Deviation Report.pdf', servedFile: served.final || null });
    }
    return { id: d.deviationNumber, documents, review: { fieldsRequire: 0, fieldsReviewed: 0 },
      uploadDate: dateOnly(d.finalReport?.timestamp || d.assessmentReport?.timestamp) };
  }).filter((r) => r.id && r.documents.length);
  if (devRows.length) sections.push({ number: sections.length + 1, title: 'Deviations', idColumn: 'Deviation ID', rows: devRows });

  const ccRows = ccs.map((cc) => {
    const served = ccDoc(cc.sponsorCCNumber);
    const name = cc.ccClosureReport?.name || `${cc.cmoCCNumber || cc.sponsorCCNumber}.Change_Control_Report.pdf`;
    return { id: cc.cmoCCNumber || cc.sponsorCCNumber, documents: [{ name, servedFile: served || null }],
      review: { fieldsRequire: 0, fieldsReviewed: 0 }, uploadDate: dateOnly(cc.ccClosureReport?.timestamp) };
  }).filter((r) => r.id);
  if (ccRows.length) sections.push({ number: sections.length + 1, title: 'Change Controls', idColumn: 'CC ID', rows: ccRows });

  return sections;
};

const SECTIONS = [
  {
    number: 1,
    title: 'Deviations',
    idColumn: 'Deviation ID',
    rows: [
      {
        id: 'D-101',
        documents: [{ name: 'Deviation Report 1.pdf' }, { name: 'Investigation Report 1.pdf' }],
        review: { fieldsRequire: 2, fieldsReviewed: 0 },
        uploadDate: '12-MAR-2026',
      },
      {
        id: 'D-102',
        documents: [{ name: 'Deviation Report 2.pdf' }, { name: 'Investigation Report 2.pdf' }],
        review: { fieldsRequire: 2, fieldsReviewed: 0 },
        uploadDate: '13-MAR-2026',
      },
    ],
  },
  {
    number: 2,
    title: 'Change Controls',
    idColumn: 'CC ID',
    rows: [
      {
        id: 'CC-1',
        documents: [{ name: 'CC Report.pdf' }],
        review: { fieldsRequire: 2, fieldsReviewed: 0 },
        uploadDate: '13-MAR-2026',
      },
    ],
  },
];


const FORM_FIELDS = [
  { label: 'DR No.', value: '01/01', score: 95 },
  { label: 'Date', value: '12/23/2025 16:04', score: 82 },
  {
    label: 'Description of Deviation',
    value: "Product: N-(4-hydroxyphenyl) Acetamide Stage C Batch No 400682 is under execution at Production Block-11 through BPR (MBPR101, Version no. 00). At Op. No. 44, during the filtration in Agitated Nutsche Filter Dryer #DNFD25-1, material passing was observed in the filtered mother liquors (MLA). This was observed by Mr. Grandhe Sriram, Production Shift In-charge on date 21-Dec-2025.",
    score: 80,
  },
  { label: 'Corrective Action Proposed', value: 'NA', score: 98 },
  {
    label: 'Preventive Action Proposed',
    value: 'The line shall be inspected as per Filter Dryer PD maintenance for FY-11 shall be revised to incorporate the following checkpoints: 1. Uniformity of the dimensions of the filter press. Replacing the filters plates on the shift-to-shift basis. 2. At a new point shall be incorporated as "if any abnormality is observed and notification is provided during the shift due to malfunction of a filter plate to an assembler using a maintenance chit within 30 minutes."',
    score: 98,
  },
  { label: 'Status', value: 'Closed', score: 98 },
  { label: 'Nature of Deviation', value: 'Major', score: null },
  { label: 'Deviation Type', value: 'Process', score: null },
  { label: 'If yes, has deviation notified?', value: 'Yes', score: null },
];

const getBadgeClass = (score, classes) => {
  if (score === null) return null;
  if (score >= 90) return classes.badgeGreen;
  if (score >= 80) return classes.badgeOrange;
  return classes.badgeRed;
};

const ReviewDetail = ({ onBack, classes }) => (
  <Box className={classes.reviewDetailRoot}>
    {/* Back bar */}
    <Box className={classes.backBar}>
      <Button className={classes.backBtn} startIcon={<ArrowBackIcon />} onClick={onBack}>Back</Button>
      <Typography className={classes.reviewTitle}>Deviation Report (Oxford Biomedica)</Typography>
    </Box>

    {/* Info bar */}
    <Box className={classes.infoBar}>
      {[
        { label: 'CMO - MFG', value: 'Oxford Biomedica - 1007' },
        { label: 'Product ID / Product Description', value: '403213 / Paracetamol 50 ml vial' },
        { label: 'CMO Work Order Number', value: 'CMO12345' },
        { label: 'Batch Number', value: '400682' },
      ].map(item => (
        <Box key={item.label} className={classes.infoBarItem}>
          <Typography className={classes.infoBarLabel}>{item.label}</Typography>
          <Typography className={classes.infoBarValue}>{item.value}</Typography>
        </Box>
      ))}
    </Box>

    {/* Split pane */}
    <Box className={classes.splitPane}>
      {/* ── LEFT: Form ── */}
      <Box className={classes.leftPane}>
        <Box className={classes.leftHeader}>
          <Typography className={classes.leftTitle}>Deviation Report Data</Typography>
          <Box>
            <Button variant="outlined" size="small" className={classes.btnEdit} startIcon={<EditIcon style={{ fontSize: '0.9rem' }} />}>Edit</Button>
            <Button variant="outlined" size="small" className={classes.btnUnedited}>Show Unedited Version</Button>
          </Box>
        </Box>

        <Paper variant="outlined" style={{ borderRadius: '4px', flex: 1, minHeight: 0 }}>
          <table className={classes.formTable}>
            <tbody>
              {FORM_FIELDS.map((field, i) => (
                <tr key={i} className={classes.formRow}>
                  <td className={classes.formLabel}>{field.label}</td>
                  <td className={classes.formValue}>
                    <Typography style={{ fontSize: '0.83rem', color: '#222', lineHeight: 1.4 }}>{field.value}</Typography>
                  </td>
                  <td style={{ width: '36px', textAlign: 'right', paddingLeft: '4px' }}>
                    {field.score !== null && (
                      <span className={`${classes.formBadge} ${getBadgeClass(field.score, classes)}`}>
                        {field.score}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Paper>

        {/* Legend */}
        <Box className={classes.legendRow}>
          <Typography className={classes.legendLabel}>Confidence Levels Legend:</Typography>
          {[
            { label: '>= 90', color: '#4CAF50' },
            { label: '80 to 90', color: '#FF8F00' },
            { label: '< 80', color: '#d32f2f' },
          ].map(l => (
            <Box key={l.label} className={classes.legendItem}>
              <Box className={classes.legendBox} style={{ backgroundColor: l.color }} />
              <span>{l.label}</span>
            </Box>
          ))}
        </Box>
        <FormControlLabel
          control={<Checkbox size="small" style={{ padding: '2px 6px' }} />}
          label={<span style={{ fontSize: '0.92rem' }}>Apply feature to all fields</span>}
          style={{ marginTop: '4px' }}
        />
        <Typography className={classes.noteText}>
          Note - This will enable editing of the high confidence level fields, highlighted in green colors.
        </Typography>
      </Box>

      {/* ── RIGHT: PDF View ── */}
      <Box className={classes.rightPane}>
        <Box className={classes.pdfHeader}>
          <Typography className={classes.pdfTitle}>PDF View</Typography>
          <Button variant="contained" size="small" className={classes.btnDownload} startIcon={<GetAppIcon style={{ fontSize: '0.9rem' }} />}>
            Download
          </Button>
        </Box>

        {/* Toolbar */}
        <Box className={classes.pdfToolbar}>
          <NavigateBeforeIcon className={classes.pdfToolbarIcon} />
          <NavigateNextIcon className={classes.pdfToolbarIcon} />
          <Typography className={classes.pdfToolbarText}>1 / 1</Typography>
          <Box style={{ flex: 1 }} />
          <ZoomOutIcon className={classes.pdfToolbarIcon} />
          <ZoomInIcon className={classes.pdfToolbarIcon} />
        </Box>

        {/* PDF Content */}
        <Box className={classes.pdfBody}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
            <Box>
              <Typography className={classes.pdfCompany}>Oxford Biomedica</Typography>
              <Typography style={{ fontSize: '0.68rem', color: '#888' }}>Corporate</Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1976d2' }} />
              <Typography className={classes.pdfLogo}>Oxford Biomedica</Typography>
            </Box>
          </Box>

          <Typography className={classes.pdfRef}>Reference SOP No. &amp; Title: 99-03, Handling of deviations</Typography>

          <Typography className={classes.pdfDocTitle}>DEVIATION REPORT</Typography>

          <Divider style={{ marginBottom: '8px' }} />

          <Typography className={classes.pdfSection}>1. 0 Deviation Details :</Typography>
          {[
            { label: 'DR Category', value: 'Production' },
            { label: 'Product Name', value: 'N-(4-hydroxyphenyl) Acetamide' },
            { label: 'Stage', value: 'C' },
          ].map(f => (
            <Box key={f.label} className={classes.pdfFieldRow}>
              <Typography className={classes.pdfFieldLabel}>{f.label}</Typography>
              <Typography className={classes.pdfFieldColon}>:</Typography>
              <Typography className={classes.pdfFieldValue}>{f.value}</Typography>
            </Box>
          ))}
          <Box className={classes.pdfFieldRow}>
            <Typography className={classes.pdfFieldLabel}>Batch No(s)</Typography>
            <Typography className={classes.pdfFieldColon}>:</Typography>
            <span className={classes.pdfHighlight}>400682</span>
          </Box>

          <Typography className={classes.pdfSection}>2.0 Deviation Observed in :</Typography>
          <Box className={classes.pdfFieldRow}>
            <Typography className={classes.pdfFieldLabel}>Deviation Type</Typography>
            <Typography className={classes.pdfFieldColon}>:</Typography>
            <span className={classes.pdfHighlightRed}>Process</span>
          </Box>
          <Box className={classes.pdfFieldRow}>
            <Typography className={classes.pdfFieldLabel}>Ref. OOS No. (if any)</Typography>
            <Typography className={classes.pdfFieldColon}>:</Typography>
            <Typography className={classes.pdfFieldValue}>N / A</Typography>
          </Box>

          <Typography className={classes.pdfSection}>3.0 Description of Deviation :</Typography>
          <Typography className={classes.pdfDesc}>
            Product: N-(4-hydroxyphenyl) Acetamide Stage C Batch No 400682 is under execution at Production Block-11 through BPR (MBPR101, Version no. 00). At Op. No. 44, during the filtration in Agitated Nutsche Filter Dryer #DNFD25-1, material passing was observed in the filtered mother liquors (MLA). This was observed by Mr. Grandhe Sriram, Production Shift In-charge on date 21-Dec-2025.
          </Typography>

          <Typography className={classes.pdfSection}>4.0 Immediate Action taken (if any) :</Typography>
          <Typography className={classes.pdfDesc}>
            1. Informed to Production Engineering, PR&amp;D, Production head and Quality Assurance Head.<br />
            2. Filtration activity has been stopped.<br />
            3. Filtered Mother Liquor with haziness (~90L) is collected in new clean containers and kept aside.
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

const DocumentsTab = ({ data, loading, rowData }) => {
  const classes = useStyles();
  const history = useHistory();

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  // Product-specific documents: API data if present, else Deviation/CC docs resolved by batch
  // (VV batches -> VV Dev/CC, TZB2401-DS -> Herceptin), else the generic static SECTIONS.
  const apiDocuments = data || [];
  const batchNumber = rowData?.batchNumber;
  const productSections = buildProductSections(batchNumber);
  const sections = apiDocuments.length > 0
    ? buildSectionsFromAPI(apiDocuments)
    : (productSections.length ? productSections : SECTIONS);

  // Batch Documents section (CoA + Batch Production Record) — opens the served /manufacturing-docs
  // PDFs directly. `servedFile` on a document routes the Download link to openDoc() instead of the
  // backend downloadReportPDF() the other (Deviation/CC) sections use.
  const batchDocRows = [];
  if (coaDoc(batchNumber)) batchDocRows.push({
    id: 'Certificate of Analysis', documents: [{ name: coaDoc(batchNumber), servedFile: coaDoc(batchNumber) }],
    review: { fieldsRequire: 0, fieldsReviewed: 0 }, uploadDate: '',
  });
  if (bprDoc(batchNumber)) batchDocRows.push({
    id: 'Batch Production Record', documents: [{ name: bprDoc(batchNumber), servedFile: bprDoc(batchNumber) }],
    review: { fieldsRequire: 0, fieldsReviewed: 0 }, uploadDate: '',
  });
  const allSections = batchDocRows.length
    ? [...sections, { number: sections.length + 1, title: 'Batch Documents', idColumn: 'Document', rows: batchDocRows }]
    : sections;

  const handleReviewClick = (section, row) => {
    const docType = section.title === 'Deviations' ? 'deviation' : 'change-control';
    sessionStorage.setItem('mfg_active_tab', 'Documents');
    const currentExpanded = sessionStorage.getItem('mfg_expanded_row');
    if (!currentExpanded) {
      console.warn('[DocumentsTab] mfg_expanded_row not in sessionStorage');
    }
    history.push(
      `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/manufacturingcollaboration/review-document/${docType}/${row.id}`,
      { section, row }
    );
  };

  return (
    <Box className={classes.root}>
      {allSections.map((section, sIdx) => (
        <Box key={section.number} className={classes.stepperRow}>
          <Box className={classes.stepperLeft}>
            <Box className={classes.stepCircle}>{section.number}</Box>
            {sIdx < allSections.length - 1 && <Box className={classes.stepLine} />}
          </Box>
          <Box className={classes.stepContent}>
            <Typography className={classes.sectionTitle}>{section.title}</Typography>
            <Paper className={classes.tableContainer} elevation={0}>
              <div style={{ width: '100%' }}>
                <Table size="small" style={{ width: '100%', tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow className={classes.headerRow}>
                      <TableCell>{section.idColumn}</TableCell>
                      <TableCell>Download Document</TableCell>
                      <TableCell>Review Digitalized Document</TableCell>
                      <TableCell>Upload Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.rows.map((row, rIdx) => (
                      <TableRow key={rIdx} className={classes.bodyRow}>
                        <TableCell className={classes.idCell}>{row.id}</TableCell>
                        <TableCell>
                          {row.documents.map((doc, dIdx) => (
                            <Link key={dIdx} className={classes.downloadLink} onClick={() => {
                              if (doc.servedFile) { openDoc(doc.servedFile); return; }
                              const docType = section.title === 'Deviations' ? 'deviation' : 'change-control';
                              downloadReportPDF(docType, doc.name.replace(/\.pdf$/i, '') + '.pdf');
                            }}>
                              <GetAppIcon className={classes.downloadIcon} />
                              {doc.name}
                            </Link>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Box className={classes.reviewBox}>
                            <StructureIcon style={{ cursor: 'pointer', flexShrink: 0, marginTop: '1px' }} onClick={() => handleReviewClick(section, row)} />
                            <Box className={classes.reviewText} style={{ cursor: 'pointer' }} onClick={() => handleReviewClick(section, row)}>
                              No. of fields that require review - {row.review.fieldsRequire}<br />
                              No. of fields reviewed - {row.review.fieldsReviewed}<br />
                              Review marked as complete - {row.review.fieldsReviewed >= row.review.fieldsRequire ? 'Yes' : 'No'}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{row.uploadDate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Paper>
          </Box>
        </Box>
      ))}

    </Box>
  );
};

export default DocumentsTab;
